import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { fetchTeacherProfile } from '../../api/teacherProfile';
import { fetchClasses } from '../../api/classes';
import { fetchCalendarLessons, fetchDraftLessons } from '../../api/lessons';
import client from '../../api/client';
import styles from './DashboardPage.module.css';

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

function isWeekend() {
  const day = new Date().getDay();
  return day === 0 || day === 6;
}

const SUGGESTIONS = [
  'Create a lesson about fractions',
  'Plan my week for 3rd grade math',
  "What standards haven't I covered yet?",
];

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [classes, setClasses] = useState([]);
  const [todayCount, setTodayCount] = useState(0);
  const [draftCount, setDraftCount] = useState(0);
  const [nextLesson, setNextLesson] = useState(null);
  const [loading, setLoading] = useState(true);

  // Chat state
  const [chatInput, setChatInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [pendingNav, setPendingNav] = useState(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    async function loadData() {
      try {
        const [profileRes, classesRes] = await Promise.all([
          fetchTeacherProfile().catch(() => null),
          fetchClasses().catch(() => ({ data: [] })),
        ]);

        const profileData = profileRes?.profile || profileRes || null;
        const classesData = classesRes?.data || classesRes?.classes || [];

        setProfile(profileData);
        setClasses(Array.isArray(classesData) ? classesData : []);

        const today = new Date().toISOString().split('T')[0];
        // For weekends, show Monday's count
        let targetDate = today;
        if (isWeekend()) {
          const d = new Date();
          const day = d.getDay();
          d.setDate(d.getDate() + (day === 6 ? 2 : 1));
          targetDate = d.toISOString().split('T')[0];
        }

        const futureDate = new Date();
        futureDate.setDate(futureDate.getDate() + 30);

        const [calRes, draftsRes] = await Promise.all([
          fetchCalendarLessons(targetDate, futureDate.toISOString().split('T')[0]).catch(() => ({ data: {} })),
          fetchDraftLessons().catch(() => ({ data: [] })),
        ]);

        const calLessons = calRes?.data?.lessons || calRes?.data || [];
        const todayLessons = Array.isArray(calLessons)
          ? calLessons.filter(l => l.scheduledDate && String(l.scheduledDate).slice(0, 10) === targetDate)
          : [];
        setTodayCount(todayLessons.length);

        const drafts = draftsRes?.data?.lessons || draftsRes?.data || [];
        setDraftCount(Array.isArray(drafts) ? drafts.length : 0);

        // Next lesson
        const upcoming = Array.isArray(calLessons)
          ? calLessons
              .filter(l => l.scheduledDate && String(l.scheduledDate).slice(0, 10) > today)
              .sort((a, b) => String(a.scheduledDate).localeCompare(String(b.scheduledDate)))
          : [];
        setNextLesson(upcoming[0] || null);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, chatLoading]);

  const sendMessage = useCallback(async (text) => {
    if (!text.trim()) return;
    const trimmed = text.trim();
    setMessages(prev => [...prev, { role: 'user', text: trimmed }]);
    setChatInput('');
    setChatLoading(true);
    setPendingNav(null);

    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await client.post('/api/chat', {
        message: trimmed,
        conversationId,
        mode: 'dashboard',
      }, { headers });

      if (response.data.text) {
        setMessages(prev => [...prev, { role: 'assistant', text: response.data.text }]);
      }
      if (response.data.conversationId) {
        setConversationId(response.data.conversationId);
      }
      if (response.data.navigation) {
        setPendingNav(response.data.navigation);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', text: 'Sorry, something went wrong. Please try again.' }]);
    } finally {
      setChatLoading(false);
    }
  }, [conversationId]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(chatInput);
    }
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <div className={styles.loading}>Loading...</div>
        </div>
      </div>
    );
  }

  const onboardingComplete = profile && profile.onboardingComplete;
  const hasClasses = classes.length > 0;
  const displayName = user?.name || '';
  const showChat = messages.length > 0;

  // New user / onboarding incomplete states
  if (!onboardingComplete) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <div className={styles.hero}>
            <h1 className={styles.greeting}>{getGreeting()}{displayName ? `, ${displayName}` : ''}.</h1>
          </div>
          <div className={styles.ctaCard}>
            <Link to="/welcome" className={styles.ctaLink}>
              <h2>Complete Your Profile</h2>
              <p>Set up your teaching profile to get personalized lesson plans.</p>
              <span className={styles.ctaBtn}>Get Started</span>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!hasClasses) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <div className={styles.hero}>
            <h1 className={styles.greeting}>{getGreeting()}{displayName ? `, ${displayName}` : ''}.</h1>
          </div>
          <div className={styles.ctaCard}>
            <Link to="/classes/new" className={styles.ctaLink}>
              <h2>Create Your First Class</h2>
              <p>Add a class to start building your scope and sequence.</p>
              <span className={styles.ctaBtn}>Create Class</span>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {/* Greeting */}
        <div className={styles.hero}>
          <h1 className={styles.greeting}>{getGreeting()}{displayName ? `, ${displayName}` : ''}.</h1>
        </div>

        {/* Command bar */}
        <div className={styles.commandBar}>
          <input
            type="text"
            className={styles.commandInput}
            placeholder="What do you want to work on?"
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <button
            className={styles.commandSend}
            onClick={() => sendMessage(chatInput)}
            disabled={chatLoading || !chatInput.trim()}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>

        {/* Suggestion chips */}
        {!showChat && (
          <div className={styles.suggestions}>
            {SUGGESTIONS.map((s, i) => (
              <button key={i} className={styles.suggestionChip} onClick={() => sendMessage(s)}>
                {s}
              </button>
            ))}
          </div>
        )}

        {/* Inline chat conversation */}
        {showChat && (
          <div className={styles.chatArea}>
            {messages.map((msg, i) => (
              <div key={i} className={`${styles.chatMsg} ${msg.role === 'user' ? styles.chatUser : styles.chatAssistant}`}>
                {msg.role === 'assistant' && <img src="/logo-notext.svg" alt="Teachio" className={styles.aiTag} />}
                <span className={styles.chatText}>{msg.text}</span>
              </div>
            ))}
            {chatLoading && (
              <div className={`${styles.chatMsg} ${styles.chatAssistant}`}>
                <img src="/logo-notext.svg" alt="Teachio" className={styles.aiTag} />
                <span className={styles.typing}>
                  <span className={styles.dot} />
                  <span className={styles.dot} />
                  <span className={styles.dot} />
                </span>
              </div>
            )}
            {pendingNav && !chatLoading && (
              <div className={styles.navConfirm}>
                <button className={styles.confirmYes} onClick={() => navigate(pendingNav.route)}>
                  Yes, take me there
                </button>
                <button className={styles.confirmNo} onClick={() => {
                  setPendingNav(null);
                  setMessages([]);
                  setConversationId(null);
                }}>
                  No thanks
                </button>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
        )}

        {/* Context cards */}
        <div className={styles.cards}>
          <Link to="/calendar" className={styles.card}>
            <span className={styles.cardLabel}>{isWeekend() ? 'Monday' : 'Today'}</span>
            <span className={styles.cardValue}>{todayCount}</span>
            <span className={styles.cardSub}>{todayCount === 1 ? 'lesson' : 'lessons'}</span>
          </Link>
          <Link to="/calendar" className={styles.card}>
            <span className={styles.cardLabel}>Drafts</span>
            {draftCount === 0 ? (
              <>
                <span className={styles.cardCheck}>&#10003;</span>
                <span className={styles.cardSub}>All scheduled</span>
              </>
            ) : (
              <>
                <span className={styles.cardValue}>{draftCount}</span>
                <span className={styles.cardSub}>to schedule</span>
              </>
            )}
          </Link>
          {nextLesson ? (
            <Link to={`/lessons/${nextLesson.id}/edit`} className={styles.card}>
              <span className={styles.cardLabel}>Next Up</span>
              <span className={styles.cardTitle}>{nextLesson.title}</span>
              <span className={styles.cardSub}>
                {new Date(nextLesson.scheduledDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              </span>
            </Link>
          ) : (
            <div className={styles.card}>
              <span className={styles.cardLabel}>Next Up</span>
              <span className={styles.cardSub}>No upcoming lessons</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
